import { formatBytes, formatDuration, h, replaceChildren } from '@/components/dom';
import { button, checkbox, select } from '@/components/controls';
import { icons } from '@/components/icons';
import type { App } from './App';
import type { ViewMode } from '@/state/appState';

export function createDebugPanel(app: App): HTMLElement {
  const table = h('table', { class: 'stat-table' });
  const view = select<ViewMode>({
    label: 'Show',
    value: app.state.ui.view,
    options: [
      { value: 'strings', label: 'Approximation (strings)' },
      { value: 'processed', label: 'Target' },
      { value: 'approximation', label: 'Engine approximation' },
      { value: 'difference', label: 'Error (signed)' },
      { value: 'heatmap', label: 'Scoring heatmap' },
    ],
    onChange: (v) => app.updateUi({ view: v }),
  });
  const ids = checkbox({ label: 'Show pin IDs', value: app.state.ui.showPinIds, onChange: (v) => app.updateUi({ showPinIds: v }) });
  const candidates = checkbox({ label: 'Show candidate lines from the current pin', value: app.state.ui.showCandidates, onChange: (v) => app.updateUi({ showCandidates: v }) });
  const refresh = button({ label: 'Refresh stats', icon: icons.refresh, onClick: () => void app.refreshStats() });
  const el = h('aside', { class: 'ed__props', hidden: !app.state.ui.debugOpen, attrs: { 'aria-label': 'Developer panel' } }, h('h3', null, 'Developer'), view.element, ids.element, candidates.element, table, refresh);

  const rowsOf = (pairs: Array<[string, string]>) => pairs.map(([k, v]) => h('tr', null, h('td', null, k), h('td', null, v)));
  app.store.subscribe((s, p) => {
    if (s.ui.debugOpen !== p.ui.debugOpen || s === p) el.hidden = !s.ui.debugOpen;
    if (!s.ui.debugOpen) return;
    if (s.ui !== p.ui || s === p) {
      view.setValue(s.ui.view);
      ids.setValue(s.ui.showPinIds);
      candidates.setValue(s.ui.showCandidates);
    }
    if (s.stats !== p.stats || s.solution !== p.solution || s.status !== p.status || s.ui.debugOpen !== p.ui.debugOpen || s === p) {
      const e = s.stats.engine;
      const c = e?.candidates ?? s.stats.prepare?.candidates ?? null;
      replaceChildren(
        table,
        ...rowsOf([
          ['Worker', s.status],
          ['WASM', s.wasmVersion ? `v${s.wasmVersion}` : 'not loaded'],
          ['Pins', String(s.solution.pins.length >>> 1)],
          ['Strings', `${s.solution.count} / ${s.config.algorithm.maxStrings}`],
          ['Current pin', String(s.solution.currentPin)],
          ['Error (RMSE)', s.solution.error.toFixed(5)],
          ['MSE', e ? e.mse.toFixed(6) : '–'],
          ['MAE', e ? e.mae.toFixed(5) : '–'],
          ['Last score', s.solution.count > 0 && s.solution.scores.length >= s.solution.count ? s.solution.scores[s.solution.count - 1].toFixed(6) : '–'],
          ['Candidates', c ? `${c.candidateCount.toLocaleString()} (${c.materialized ? 'materialized' : 'implicit'}, ${c.cached ? 'cached' : 'on the fly'})` : '–'],
          ['Cached pixels', c ? c.cachedPixels.toLocaleString() : '–'],
          ['Engine memory', e ? formatBytes(e.memoryBytes) : '–'],
          ['WASM heap', formatBytes(s.stats.wasmMemoryBytes)],
          ['Strategy', e?.strategy ?? s.config.algorithm.strategy],
          ['Steps / s', s.stats.stepsPerSecond.toFixed(0)],
          ['Batch size', String(s.stats.batchSize)],
          ['Elapsed', formatDuration(s.stats.elapsedMs)],
          ['Prepare time', formatDuration(s.stats.prepareMs)],
          ['Load time', formatDuration(s.stats.loadMs)],
          ['Render time', `${s.stats.renderMs.toFixed(1)} ms`],
          ['FPS', String(s.stats.fps)],
          ['Stop reason', s.solution.stopReason ?? '–'],
        ]),
      );
    }
  }, { immediate: true });
  return el;
}
